import { brand } from "../../theme/theme";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { MessageSquare, RefreshCw, Circle, ArrowRight, ChevronDown } from "lucide-react";

import apiClient, { sendMessageWithAttachments } from "../../api-client";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { ThreadPost } from "./ThreadPost";
import { ArchiveEventMessagesModal } from "./ArchiveEventMessagesModal";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { ThreadComposer } from "./ThreadComposer";
import { EditTaskModal } from "../TechnicalSetup/EditTaskModal";
import { CustomSelector } from "../../components/CustomSelector";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { isClientReadOnly } from "../../utils/clientAccess";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";

const PAGE_SIZE = 30;

const extractPageNumber = (url) => {
  if (!url) return null;
  if (typeof url === "number") {
    return Number.isFinite(url) ? Number(url) : null;
  }

  const stringUrl = String(url);

  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://placeholder";
    const parsed = new URL(stringUrl, base);
    const pageParam = parsed.searchParams.get("page");
    return pageParam ? Number(pageParam) : null;
  } catch (error) {
    const match = stringUrl.match(/page=(\d+)/);
    return match ? Number(match[1]) : null;
  }
};

const mapMessageToPost = (message) => {
  const replies = Array.isArray(message.replies) ? message.replies : [];

  return {
    id: message.id,
    threadId: message.thread, // Añadir threadId para lazy loading de participants
    authorId: message.sender?.id || "system",
    author: message.sender?.full_name || message.author_display_name || "Sistema",
    authorRole: message.sender?.role || "system",
    authorProfileImage: getAvatarImageUrl(message.sender?.profile_image),
    content: message.content,
    timestamp: message.created_at,
    attachments: message.attachments || [],
    tags: message.tags || [],
    comments: replies.map((reply) => ({
      id: reply.id,
      authorId: reply.sender?.id || "unknown",
      author: reply.sender?.full_name || reply.author_display_name || "Usuario",
      authorRole: reply.sender?.role || "user",
      authorProfileImage: getAvatarImageUrl(reply.sender?.profile_image),
      content: reply.content,
      timestamp: reply.created_at,
      attachments: reply.attachments || [],
      parent: reply.parent,
      is_liked: reply.is_liked || false,
      like_count: reply.like_count || 0,
      liked_by: reply.liked_by || [],
    })),
    liked: message.is_liked || false,
    likeCount: message.like_count || 0,
    likedBy: message.liked_by || [],
    messageType: message.message_type,
    isResolved: message.is_resolved,
    actionAssignedTo: message.action_assigned_to,
    taskId: message.task_id,
  };
};

const formatDateTime = (isoString) => {
  if (!isoString) return "Fecha desconocida";
  try {
    const date = new Date(isoString);
    return date.toLocaleString();
  } catch (error) {
    return isoString;
  }
};

const getClientLabel = (message) => {
  if (message.client_name) return message.client_name;
  if (message.client_id) return `Cliente #${message.client_id}`;
  return "Cliente sin asignar";
};

const getThreadLabel = (message) => {
  if (message?.is_thread) {
    const synchronizedTitle = (message?.is_thread_title || "").trim();
    if (synchronizedTitle) return synchronizedTitle;
  }
  if (message.thread_title) return message.thread_title;
  if (message.thread) return `Conversación principal`;
  return "Conversación";
};

const getMessageConsultants = (message) => {
  // Intentar obtener participantes del thread
  if (
    message.thread_participants &&
    Array.isArray(message.thread_participants)
  ) {
    return message.thread_participants.filter(
      (participant) =>
        participant.role === "consultant" ||
        participant.role === "agency_admin" ||
        participant.role === "superadmin"
    );
  }

  // Si no hay thread_participants, retornar array vacío
  // Los consultants serán todos los usuarios disponibles del sistema
  return [];
};

const getClientById = (clientId, clients) => {
  if (!clientId || !Array.isArray(clients)) return null;
  return clients.find(client => String(client.id) === String(clientId));
};

const normalizeSelectedClientId = (value) => {
  if (Array.isArray(value)) {
    if (value.length !== 1) return null;

    const singleValue = value[0];
    if (singleValue && typeof singleValue === "object") {
      return singleValue.id ?? singleValue.client_id ?? singleValue.clientId ?? null;
    }

    return singleValue ?? null;
  }

  if (value && typeof value === "object") {
    return value.id ?? value.client_id ?? value.clientId ?? null;
  }

  return value ?? null;
};

export function GlobalMessageWall({
  onMessageOpen,
  refreshKey,
  filterRefresh,
  currentUser,
  interceptorReady,
  enabled = true,
  clients = [],
  onShowNotification = null,
  selectedFilter = null,
  userRole = null,
  selectedClientId = null,
  showSuperadminSelector = false,
  superadminSelectorValue = null,
  superadminClientOptions = [],
  onSuperadminClientSelect = null,
  conversationFontSize = null, // Tamaño de letra personalizado en píxeles
  forceShowClientMeta = false,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadedPages, setLoadedPages] = useState([]);
  const [pageNavigation, setPageNavigation] = useState({});
  const [error, setError] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Estados para el thread general del cliente seleccionado
  const [generalThread, setGeneralThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);

  // Estados para el modal de asignar tarea
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalMessage, setTaskModalMessage] = useState(null);
  const [taskModalTask, setTaskModalTask] = useState(null);
  const [taskModalConsultants, setTaskModalConsultants] = useState([]);
  const [archiveEventModalOpen, setArchiveEventModalOpen] = useState(false);
  const [archiveEventTargetMessage, setArchiveEventTargetMessage] =
    useState(null);
  const [archiveEventRecommendations, setArchiveEventRecommendations] =
    useState([]);
  const [selectedArchiveEventIds, setSelectedArchiveEventIds] = useState([]);
  const [isArchivingEventMessages, setIsArchivingEventMessages] =
    useState(false);

  const loadingPagesRef = useRef(new Set());
  const pageMessagesRef = useRef(new Map());
  const pageNavigationRef = useRef({});
  const messageRefs = useRef(new Map());
  const scrollContainerRef = useRef(null);
  const loadSessionRef = useRef(0);
  const lastInitialLoadKeyRef = useRef(null);
  const initialLoadInFlightRef = useRef(null);
  const archiveEventRequestRef = useRef(0);

  const canFetch = Boolean(interceptorReady && enabled);
  const resolvedSelectedClientId = useMemo(
    () => normalizeSelectedClientId(selectedClientId),
    [selectedClientId]
  );
  const initialLoadKey = useMemo(
    () =>
      JSON.stringify({
        refreshKey: refreshKey ?? 0,
        filterRefresh: filterRefresh ?? 0,
        selectedFilter: selectedFilter ?? null,
        selectedClientId: resolvedSelectedClientId ?? null,
        userRole: userRole ?? null,
      }),
    [refreshKey, filterRefresh, selectedFilter, resolvedSelectedClientId, userRole]
  );

  const buildGlobalWallParams = useCallback(
    (page) => {
      const params = {
        page,
        page_size: PAGE_SIZE,
      };

      const effectiveClientId =
        resolvedSelectedClientId ||
        ((userRole === "agency_admin" || userRole === "consultant") &&
        selectedFilter
          ? selectedFilter
          : null);

      if (effectiveClientId) {
        params.client_id = effectiveClientId;
      } else if (selectedFilter && userRole === "superadmin") {
        params.agency = selectedFilter;
      }

      return params;
    },
    [resolvedSelectedClientId, selectedFilter, userRole]
  );

  const mergeMessages = useCallback((existingMessages, incomingMessages) => {
    const existingIds = new Set(existingMessages.map((msg) => String(msg.id)));

    // Crear un map con los mensajes entrantes para actualizaciones
    const incomingMap = new Map();
    incomingMessages.forEach((message) => {
      incomingMap.set(String(message.id), message);
    });

    // Actualizar mensajes existentes si hay versiones nuevas, manteniendo el orden
    const updatedExisting = existingMessages.map((msg) => {
      const updated = incomingMap.get(String(msg.id));
      return updated || msg;
    });

    // Agregar mensajes nuevos que no existían (al final, ya vienen ordenados del backend)
    const newMessages = incomingMessages.filter(
      (msg) => !existingIds.has(String(msg.id))
    );

    return [...updatedExisting, ...newMessages];
  }, []);

  const resetPagingState = useCallback(() => {
    loadSessionRef.current += 1;
    setMessages([]);
    setLoadedPages([]);
    setPageNavigation({});
    loadingPagesRef.current.clear();
    pageMessagesRef.current.clear();
    pageNavigationRef.current = {};
    setInitialLoaded(false);
    setError(null);
    setLoadingPage(false);
  }, []);

  const loadPage = useCallback(
    async (page, { replace = false } = {}) => {
      if (!page || page < 1 || !canFetch) return null;

      const sessionId = loadSessionRef.current;

      if (loadingPagesRef.current.has(page)) {
        return pageNavigationRef.current[page] || null;
      }

      loadingPagesRef.current.add(page);
      setLoadingPage(true);
      setError(null);

      try {
        const response = await apiClient.get("communication/messages/global/", {
          params: buildGlobalWallParams(page),
        });

        if (sessionId !== loadSessionRef.current) {
          return null;
        }

        const results = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];

        const normalized = results.map((message) => ({
          ...message,
          replies: Array.isArray(message.replies) ? message.replies : [],
        }));

        const previousIds = pageMessagesRef.current.get(page) || [];

        setMessages((prevMessages) => {
          let baseMessages = replace ? [] : [...prevMessages];

          if (!replace && previousIds.length > 0) {
            const idsToRemove = new Set(previousIds);
            baseMessages = baseMessages.filter(
              (message) => !idsToRemove.has(String(message.id))
            );
          }

          return mergeMessages(baseMessages, normalized);
        });

        const normalizedIds = normalized.map((message) => String(message.id));
        pageMessagesRef.current.set(page, normalizedIds);

        setLoadedPages((prev) => {
          const nextSet = replace ? new Set() : new Set(prev);
          nextSet.add(page);
          return Array.from(nextSet).sort((a, b) => a - b);
        });

        const previousPageNumber = extractPageNumber(response.data?.previous);
        const nextPageNumber = extractPageNumber(response.data?.next);

        setPageNavigation((prev) => {
          const nextNavigation = replace ? {} : { ...prev };
          nextNavigation[page] = {
            previous: previousPageNumber,
            next: nextPageNumber,
          };
          pageNavigationRef.current = nextNavigation;
          return nextNavigation;
        });

        setInitialLoaded(true);

        return {
          previousPage: previousPageNumber,
          nextPage: nextPageNumber,
        };
      } catch (fetchError) {
        console.error("Error cargando mensajes globales", fetchError);
        if (sessionId === loadSessionRef.current) {
          if (replace) {
            setMessages([]);
            setLoadedPages([]);
            setPageNavigation({});
            pageMessagesRef.current.clear();
            pageNavigationRef.current = {};
            setInitialLoaded(false);
          }
          setError("No se pudieron cargar los mensajes recientes.");
        }
        return null;
      } finally {
        if (sessionId === loadSessionRef.current) {
          loadingPagesRef.current.delete(page);
          if (loadingPagesRef.current.size === 0) {
            setLoadingPage(false);
          }
        }
      }
    },
    [buildGlobalWallParams, canFetch, mergeMessages]
  );

  useEffect(() => {
    if (!canFetch) {
      return;
    }

    if (initialLoadInFlightRef.current) {
      lastInitialLoadKeyRef.current = initialLoadKey;
      return;
    }

    if (lastInitialLoadKeyRef.current === initialLoadKey) {
      return;
    }

    lastInitialLoadKeyRef.current = initialLoadKey;
    initialLoadInFlightRef.current = initialLoadKey;
    resetPagingState();

    loadPage(1, { replace: true })
      .catch((fetchError) => {
        console.error("Error cargando la página inicial de mensajes", fetchError);
      })
      .finally(() => {
        if (initialLoadInFlightRef.current === initialLoadKey) {
          initialLoadInFlightRef.current = null;
        }
      });
  }, [loadPage, canFetch, resetPagingState, initialLoadKey]);

  // Effect para cargar el thread general cuando hay un cliente seleccionado
  useEffect(() => {
    const fetchGeneralThread = async () => {
      const targetClientId =
        resolvedSelectedClientId ||
        ((userRole === "agency_admin" || userRole === "consultant")
          ? selectedFilter
          : null);

      const allowedRoles = ["client", "consultant", "agency_admin", "superadmin"];

      if (!canFetch || !targetClientId || !allowedRoles.includes(userRole)) {
        setGeneralThread(null);
        return;
      }

      setLoadingThread(true);
      try {
        const response = await apiClient.get("communication/threads/", {
          params: { client: targetClientId },
        });

        const threads = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];

        // Buscar el thread de tipo "general"
        const general = threads.find((thread) => thread.type === "general");

        if (general) {
          setGeneralThread(general);
        } else {
          setGeneralThread(null);
          console.warn("No se encontró thread general para el cliente:", targetClientId);
        }
      } catch (error) {
        console.error("Error cargando thread general:", error);
        setGeneralThread(null);
      } finally {
        setLoadingThread(false);
      }
    };

    fetchGeneralThread();
  }, [canFetch, selectedFilter, resolvedSelectedClientId, userRole, refreshKey]);

  const earliestPage = loadedPages.length > 0 ? loadedPages[0] : null;
  const latestPage =
    loadedPages.length > 0 ? loadedPages[loadedPages.length - 1] : null;

  const nextPageToLoad =
    latestPage !== null ? pageNavigation[latestPage]?.next ?? null : null;
  const hasNextPage = Boolean(nextPageToLoad);

  const isLoadingMore = loadingPage && messages.length > 0;
  const isInitialLoading = loadingPage && !initialLoaded && messages.length === 0;

  const handleLoadMore = useCallback(() => {
    if (!nextPageToLoad || nextPageToLoad < 1) return;
    if (loadedPages.includes(nextPageToLoad)) return;
    loadPage(nextPageToLoad);
  }, [nextPageToLoad, loadedPages, loadPage]);

  const handleRetry = useCallback(() => {
    if (!canFetch) return;
    resetPagingState();
    loadPage(1, { replace: true });
  }, [loadPage, canFetch, resetPagingState]);

  const setMessageRef = useCallback((messageId, element) => {
    const normalizedId = String(messageId);

    if (element) {
      messageRefs.current.set(normalizedId, element);
    } else {
      messageRefs.current.delete(normalizedId);
    }
  }, []);

  const getCurrentViewportPage = useCallback(() => {
    const loadedPageList = loadedPages;
    if (!loadedPageList.length) {
      return 1;
    }

    const containerElement = scrollContainerRef.current;
    if (!containerElement) {
      return loadedPageList[0];
    }

    const containerRect = containerElement.getBoundingClientRect();
    let bestMatch = {
      page: loadedPageList[0],
      distance: Number.POSITIVE_INFINITY,
    };

    loadedPageList.forEach((page) => {
      const pageMessageIds = pageMessagesRef.current.get(page) || [];

      pageMessageIds.forEach((messageId) => {
        const element = messageRefs.current.get(String(messageId));
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top - containerRect.top);

        if (distance < bestMatch.distance) {
          bestMatch = { page, distance };
        }
      });
    });

    return bestMatch.page || 1;
  }, [loadedPages]);

  const closeArchiveEventModal = useCallback(() => {
    archiveEventRequestRef.current += 1;
    setArchiveEventModalOpen(false);
    setArchiveEventTargetMessage(null);
    setArchiveEventRecommendations([]);
    setSelectedArchiveEventIds([]);
    setIsArchivingEventMessages(false);
  }, []);

  const handleOpenMessage = useCallback(
    (message) => {
      if (!onMessageOpen) {
        return;
      }

      if (!message) {
        return;
      }

      // Pasar el mensaje directamente como espera handleGlobalMessageOpen
      onMessageOpen(message);
    },
    [onMessageOpen]
  );

  // Función para manejar likes en posts con actualización optimista
  const handleLikePost = useCallback(
    async (messageId) => {
      let targetMessage = null;
      let isComment = false;
      let parentPostId = null;

      // Buscar en mensajes raíz
      targetMessage = messages.find((msg) => String(msg.id) === String(messageId));

      if (!targetMessage) {
        // Buscar en comentarios de todos los mensajes
        for (const msg of messages) {
          const comment = (msg.replies || []).find(
            (reply) => String(reply.id) === String(messageId)
          );
          if (comment) {
            targetMessage = comment;
            isComment = true;
            parentPostId = msg.id;
            break;
          }
        }
      }

      if (!targetMessage) return;

      // Estado anterior para rollback en caso de error
      const previousLiked = targetMessage.is_liked;
      const previousLikeCount = targetMessage.like_count;
      const previousLikedBy = targetMessage.liked_by || [];

      // Actualización optimista
      const wasLiked = targetMessage.is_liked;
      const newLiked = !wasLiked;
      const newLikeCount = wasLiked
        ? Math.max(0, (targetMessage.like_count || 0) - 1)
        : (targetMessage.like_count || 0) + 1;
      const currentUserEntry = currentUser
        ? { full_name: currentUser.full_name || currentUser.name || "", profile_image: currentUser.profile_image || null }
        : null;
      const newLikedBy = wasLiked
        ? previousLikedBy.filter((u) => u.full_name !== (currentUserEntry?.full_name))
        : currentUserEntry
        ? [...previousLikedBy, currentUserEntry]
        : previousLikedBy;

      // Actualizar estado local
      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (!isComment) {
            // Es un post raíz
            return String(msg.id) === String(messageId)
              ? { ...msg, is_liked: newLiked, like_count: newLikeCount, liked_by: newLikedBy }
              : msg;
          } else {
            // Es un comentario, buscar el padre
            if (String(msg.id) !== String(parentPostId)) return msg;

            const updatedReplies = (msg.replies || []).map((reply) =>
              String(reply.id) === String(messageId)
                ? { ...reply, is_liked: newLiked, like_count: newLikeCount, liked_by: newLikedBy }
                : reply
            );

            return { ...msg, replies: updatedReplies };
          }
        })
      );

      try {
        await apiClient.post(`communication/messages/${messageId}/like/`);
      } catch (error) {
        console.error("Error al dar like:", error);
        // Revertir en caso de error
        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (!isComment) {
              return String(msg.id) === String(messageId)
                ? { ...msg, is_liked: previousLiked, like_count: previousLikeCount, liked_by: previousLikedBy }
                : msg;
            } else {
              if (String(msg.id) !== String(parentPostId)) return msg;

              const revertedReplies = (msg.replies || []).map((reply) =>
                String(reply.id) === String(messageId)
                  ? { ...reply, is_liked: previousLiked, like_count: previousLikeCount, liked_by: previousLikedBy }
                  : reply
              );

              return { ...msg, replies: revertedReplies };
            }
          })
        );
      }
    },
    [messages, currentUser]
  );

  // Función para actualizar el tipo de mensaje (importante/acción)
  const handleUpdateMessageType = useCallback(
    async (messageId, newType, assignedTo = null) => {
      const currentMessage = messages.find(
        (msg) => String(msg.id) === String(messageId)
      );
      if (!currentMessage) return;

      const previousMessageType = currentMessage.message_type;
      const previousActionAssignedTo = currentMessage.action_assigned_to;

      // Actualización optimista
      const optimisticUpdate = {};

      if (newType === "action" && assignedTo) {
        optimisticUpdate.message_type = "action";
        optimisticUpdate.action_assigned_to = assignedTo.id;
      } else {
        optimisticUpdate.message_type = newType;
        if (newType !== "action") {
          optimisticUpdate.action_assigned_to = null;
        }
      }

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? { ...msg, ...optimisticUpdate }
            : msg
        )
      );

      try {
        const updateData = {};

        if (newType === "action" && assignedTo) {
          updateData.action_assigned_to = assignedTo.id;
        } else {
          updateData.message_type = newType;
        }

        const response = await apiClient.patch(
          `communication/messages/${messageId}/`,
          updateData
        );

        if (response?.data) {
          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              String(msg.id) === String(messageId)
                ? { ...msg, ...response.data }
                : msg
            )
          );
        }
      } catch (error) {
        console.error("Error al actualizar mensaje:", error);
        // Revertir cambios locales
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            String(msg.id) === String(messageId)
              ? {
                ...msg,
                message_type: previousMessageType,
                action_assigned_to: previousActionAssignedTo,
              }
              : msg
          )
        );
      }
    },
    [messages]
  );

  const handleEditMessage = useCallback(async (messageId, newContent) => {
    if (!newContent || !newContent.trim()) {
      throw new Error("El contenido del mensaje no puede estar vacío.");
    }

    try {
      await apiClient.patch(`communication/messages/${messageId}/`, {
        content: newContent,
      });

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? { ...msg, content: newContent }
            : msg
        )
      );
    } catch (error) {
      console.error("Error al actualizar mensaje:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Error al actualizar el mensaje. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  }, []);

  const handleDeleteMessage = useCallback(async (messageId) => {
    try {
      await apiClient.delete(`communication/messages/${messageId}/`);

      setMessages((prevMessages) =>
        prevMessages.filter((msg) => String(msg.id) !== String(messageId))
      );
    } catch (error) {
      console.error("Error al eliminar mensaje:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Error al eliminar el mensaje. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  }, []);

  const handleEditComment = useCallback(
    async ({ commentId, parentId, content }) => {
      if (!content || !content.trim()) {
        throw new Error("El contenido del comentario no puede estar vacío.");
      }

      try {
        await apiClient.patch(`communication/messages/${commentId}/`, {
          content,
        });

        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (String(msg.id) !== String(parentId)) {
              return msg;
            }

            const updatedReplies = (msg.replies || []).map((reply) =>
              String(reply.id) === String(commentId)
                ? { ...reply, content }
                : reply
            );

            return {
              ...msg,
              replies: updatedReplies,
            };
          })
        );
      } catch (error) {
        console.error("Error al actualizar comentario:", error);
        const errorMessage =
          error.response?.data?.detail ||
          "Error al actualizar el comentario. Intenta nuevamente.";
        throw new Error(errorMessage);
      }
    },
    [messages]
  );

  const handleDeleteComment = useCallback(
    async ({ commentId, parentId }) => {
      try {
        await apiClient.delete(`communication/messages/${commentId}/`);

        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (String(msg.id) !== String(parentId)) {
              return msg;
            }

            const updatedReplies = (msg.replies || []).filter(
              (reply) => String(reply.id) !== String(commentId)
            );

            return {
              ...msg,
              replies: updatedReplies,
            };
          })
        );
      } catch (error) {
        console.error("Error al eliminar comentario:", error);
        const errorMessage =
          error.response?.data?.detail ||
          "Error al eliminar el comentario. Intenta nuevamente.";
        throw new Error(errorMessage);
      }
    },
    [messages]
  );

  // Función para manejar comentarios en posts
  const handleCommentPost = useCallback(
    async (postId, comment, attachments = []) => {
      const parentMessage = messages.find(
        (msg) => String(msg.id) === String(postId)
      );
      if (!parentMessage) return;

      const threadId =
        parentMessage.thread?.id ||
        parentMessage.thread_id ||
        (typeof parentMessage.thread === "number"
          ? parentMessage.thread
          : null);

      if (!threadId || (!comment.trim() && attachments.length === 0)) return;

      try {
        let response;
        if (attachments.length > 0) {
          // Usar la función de envío con adjuntos para comentarios
          const formData = new FormData();
          formData.append("thread", threadId);
          formData.append("content", comment);
          formData.append("parent", postId); // ID del mensaje padre

          // Añadir archivos adjuntos
          attachments.forEach((file) => {
            formData.append("attachments", file);
          });

          response = await apiClient.post(
            "/communication/messages/",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        } else {
          // Envío tradicional sin adjuntos
          const newComment = {
            thread: threadId,
            parent: postId, // ID del mensaje padre
            content: comment,
          };

          response = await apiClient.post(
            "communication/messages/",
            newComment
          );
        }

        const createdComment = response?.data ?? response;

        if (createdComment?.id) {
          // Actualizar el mensaje padre con el nuevo comentario
          setMessages((prevMessages) =>
            prevMessages.map((msg) => {
              if (String(msg.id) !== String(postId)) {
                return msg;
              }

              const existingReplies = Array.isArray(msg.replies)
                ? msg.replies
                : [];
              const commentExists = existingReplies.some(
                (reply) => String(reply.id) === String(createdComment.id)
              );

              const updatedReplies = commentExists
                ? existingReplies.map((reply) =>
                  String(reply.id) === String(createdComment.id)
                    ? createdComment
                    : reply
                )
                : [...existingReplies, createdComment];

              return {
                ...msg,
                replies: updatedReplies,
              };
            })
          );
        }
      } catch (error) {
        console.error("Error al enviar el comentario:", error);

        // Manejo específico de errores del backend
        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 400:
              throw new Error(
                data.detail ||
                "Los datos del comentario no son válidos. Verifica el contenido."
              );
            case 401:
              throw new Error(
                "Tu sesión ha expirado. Por favor, inicia sesión nuevamente."
              );
            case 403:
              throw new Error(
                "No tienes permisos para participar en esta conversación."
              );
            case 404:
              throw new Error(
                "El mensaje o la conversación no fue encontrada."
              );
            case 413:
              throw new Error(
                "El comentario o los archivos adjuntos son demasiado grandes."
              );
            default:
              throw new Error(
                "Error del servidor. Intenta nuevamente en unos momentos."
              );
          }
        }

        // Error de conexión o desconocido
        throw new Error(
          "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
        );
      }
    },
    [messages]
  );

  const handleDeleteAttachment = useCallback(
    ({ messageId, attachmentId, parentId }) => {
      if (!attachmentId) return;

      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          const messageIdStr = String(messageId);
          const parentIdStr = parentId ? String(parentId) : null;
          const currentIdStr = String(msg.id);

          if (currentIdStr === messageIdStr) {
            return {
              ...msg,
              attachments: (msg.attachments || []).filter(
                (attachment) =>
                  String(attachment.id) !== String(attachmentId)
              ),
            };
          }

          if (parentIdStr && currentIdStr === parentIdStr) {
            const updatedReplies = (msg.replies || []).map((reply) => {
              if (String(reply.id) !== messageIdStr) {
                return reply;
              }

              return {
                ...reply,
                attachments: (reply.attachments || []).filter(
                  (attachment) =>
                    String(attachment.id) !== String(attachmentId)
                ),
              };
            });

            return {
              ...msg,
              replies: updatedReplies,
            };
          }

          return msg;
        })
      );
    },
    []
  );

  const handleToggleArchiveEventSelection = useCallback((messageId) => {
    setSelectedArchiveEventIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId]
    );
  }, []);

  const handleOpenArchiveEventModal = useCallback(
    async (messageId) => {
      const targetMessage = messages.find(
        (message) => String(message.id) === String(messageId)
      );
      if (!targetMessage) return;

      const requestId = archiveEventRequestRef.current + 1;
      archiveEventRequestRef.current = requestId;

      try {
        const currentPage = getCurrentViewportPage();
        const response = await apiClient.get("communication/messages/global/", {
          params: buildGlobalWallParams(currentPage),
        });

        if (archiveEventRequestRef.current !== requestId) {
          return;
        }

        const currentPageMessages = Array.isArray(response?.data?.results)
          ? response.data.results
          : [];

        const recommendations = currentPageMessages.filter(
          (message) =>
            String(message?.id) !== String(messageId) &&
            message?.message_type === "event" &&
            String(message?.client_id) === String(targetMessage?.client_id)
        );

        if (archiveEventRequestRef.current !== requestId) {
          return;
        }

        setArchiveEventTargetMessage(targetMessage);
        setArchiveEventRecommendations(recommendations);
        setSelectedArchiveEventIds([]);
        setArchiveEventModalOpen(true);
      } catch (error) {
        console.error("Error loading archive event recommendations:", error);
        if (archiveEventRequestRef.current === requestId) {
          setArchiveEventTargetMessage(targetMessage);
          setArchiveEventRecommendations([]);
          setSelectedArchiveEventIds([]);
          setArchiveEventModalOpen(true);
        }
      }
    },
    [buildGlobalWallParams, getCurrentViewportPage, messages]
  );

  const handleConfirmArchiveEventMessages = useCallback(async () => {
    if (!archiveEventTargetMessage?.id || isArchivingEventMessages) return;

    const idsToArchive = [
      archiveEventTargetMessage.id,
      ...selectedArchiveEventIds,
    ];

    setIsArchivingEventMessages(true);

    try {
      await apiClient.post("communication/messages/archive/", {
        message_ids: idsToArchive,
      });

      const archivedIdSet = new Set(idsToArchive.map((id) => String(id)));

      setMessages((prevMessages) =>
        prevMessages
          .filter((message) => !archivedIdSet.has(String(message.id)))
          .map((message) => ({
            ...message,
            replies: (message.replies || []).filter(
              (reply) =>
                !archivedIdSet.has(String(reply.id)) &&
                !archivedIdSet.has(String(reply.parent))
            ),
          }))
      );

      closeArchiveEventModal();

      if (onShowNotification) {
        onShowNotification({
          message:
            idsToArchive.length > 1
              ? "Mensajes archivados correctamente."
              : "Mensaje archivado correctamente.",
          isError: false,
        });
      }
    } catch (error) {
      console.error("Error archiving event messages:", error);
      const rawError =
        error?.response?.data?.detail ||
        error?.response?.data?.message_ids ||
        "No se pudieron archivar los mensajes seleccionados.";
      if (onShowNotification) {
        onShowNotification({
          message: Array.isArray(rawError) ? rawError.join(", ") : rawError,
          isError: true,
        });
      }
    } finally {
      setIsArchivingEventMessages(false);
    }
  }, [
    archiveEventTargetMessage,
    closeArchiveEventModal,
    isArchivingEventMessages,
    onShowNotification,
    selectedArchiveEventIds,
  ]);

  // Función para enviar un nuevo mensaje al thread general
  const handlePostToGeneralThread = useCallback(
    async (content, messageType, selectedConsultant, attachments = []) => {
      if (!generalThread?.id) {
        throw new Error("No se encontró el thread general del cliente");
      }

      try {
        let response;

        if (attachments.length > 0) {
          const formData = new FormData();
          formData.append("thread", generalThread.id);
          formData.append("content", content);

          if (messageType === "action" && selectedConsultant) {
            formData.append("action_assigned_to", selectedConsultant.id || selectedConsultant);
          } else if (messageType === "important") {
            formData.append("message_type", "important");
          }

          attachments.forEach((file) => {
            formData.append("attachments", file);
          });

          response = await apiClient.post(
            "/communication/messages/",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        } else {
          const messageData = {
            thread: generalThread.id,
            content,
          };

          if (messageType === "action" && selectedConsultant) {
            messageData.action_assigned_to = selectedConsultant.id || selectedConsultant;
          } else if (messageType === "important") {
            messageData.message_type = "important";
          }

          response = await apiClient.post(
            "communication/messages/",
            messageData
          );
        }

        const createdMessage = response?.data ?? response;

        if (createdMessage?.id) {
          // Resetear y recargar los mensajes del muro global
          resetPagingState();
          await loadPage(1, { replace: true });
        }
      } catch (error) {
        console.error("Error al enviar el mensaje:", error);

        let errorMessage = "Error al enviar el mensaje. Intenta nuevamente.";

        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 400:
              errorMessage = data.detail || "Los datos del mensaje no son válidos.";
              break;
            case 401:
              errorMessage = "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.";
              break;
            case 403:
              errorMessage = "No tienes permisos para enviar mensajes en esta conversación.";
              break;
            case 404:
              errorMessage = "La conversación no fue encontrada.";
              break;
            case 413:
              errorMessage = "El mensaje o los archivos adjuntos son demasiado grandes.";
              break;
            default:
              errorMessage = "Error del servidor. Intenta nuevamente en unos momentos.";
          }
        }

        throw new Error(errorMessage);
      }
    },
    [generalThread, resetPagingState, loadPage]
  );

  // Función para asignar tarea - IDÉNTICO a ThreadInterface
  const handleOpenTaskModal = useCallback(
    (post, consultantsOverride = null) => {
      if (!post) return;
      const consultantList =
        Array.isArray(consultantsOverride) && consultantsOverride.length > 0
          ? consultantsOverride
          : generalThread?.participants || [];
      setTaskModalConsultants(consultantList);

      const initialAssigned =
        (typeof post.actionAssignedTo === "object" &&
          post.actionAssignedTo !== null
          ? post.actionAssignedTo.id
          : post.actionAssignedTo) || "";

      setTaskModalMessage(post);
      setTaskModalTask({
        id: post.taskId || null,
        title: "",
        description: "",
        assigned_to: initialAssigned ? String(initialAssigned) : "",
        deadline: null,
        documentation_urls: [],
      });
      setTaskModalOpen(true);

      if (post.taskId) {
        apiClient
          .get(`projects/tasks/${post.taskId}/`)
          .then((response) => {
            if (response?.data) {
              setTaskModalTask(response.data);
            }
          })
          .catch((error) => {
            console.error("Error al obtener tarea:", error);
            if (onShowNotification) {
              onShowNotification({
                message:
                  error?.response?.data?.detail ||
                  "No se pudo cargar la tarea del mensaje.",
                isError: true,
              });
            }
          });
      }
    },
    [generalThread, onShowNotification]
  );

  const handleCloseTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setTaskModalMessage(null);
    setTaskModalTask(null);
    setTaskModalConsultants([]);
  }, []);

  // Helper para resolver el objeto de usuario asignado
  const resolveAssigneeObject = useCallback(
    (assignedToId) => {
      if (!assignedToId) return null;

      const participants = generalThread?.participants || [];
      const assignee = participants.find(
        (p) => String(p.id) === String(assignedToId)
      );

      return assignee || null;
    },
    [generalThread]
  );

  const handleConversationTaskSubmit = useCallback(
    async ({ task, values }) => {
      if (!taskModalMessage) {
        throw new Error("No hay mensaje seleccionado.");
      }

      const messageId = taskModalMessage.id;
      const assignedToId = values.assigned_to;

      if (!assignedToId) {
        throw new Error("Debes seleccionar un responsable para la tarea.");
      }

      const extractTaskId = (data) => {
        if (!data) return null;
        if (typeof data.task_id !== "undefined" && data.task_id !== null) {
          return data.task_id;
        }
        if (data.task && typeof data.task === "object") {
          return data.task.id ?? null;
        }
        return null;
      };

      let currentTaskId =
        task?.id || taskModalMessage.taskId || taskModalTask?.id || null;

      if (!currentTaskId) {
        const response = await apiClient.patch(
          `communication/messages/${messageId}/`,
          {
            message_type: "action",
            action_assigned_to: assignedToId,
          }
        );

        if (response?.data) {
          currentTaskId = extractTaskId(response.data);
        }
      }

      let taskResponse = null;
      if (currentTaskId) {
        taskResponse = await apiClient.patch(
          `projects/tasks/${currentTaskId}/`,
          values
        );

        if (taskResponse?.data) {
          setTaskModalTask(taskResponse.data);
        }
      } else {
        console.warn(
          `No se pudo obtener el ID de tarea para el mensaje ${messageId}. Se actualiza solo el mensaje.`
        );
      }

      const assignedUser = resolveAssigneeObject(assignedToId);

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? {
              ...msg,
              message_type: "action",
              action_assigned_to: assignedUser || assignedToId,
              task_id: currentTaskId || null,
            }
            : msg
        )
      );

      setTaskModalMessage((prev) =>
        prev
          ? {
            ...prev,
            messageType: "action",
            actionAssignedTo: assignedUser || assignedToId,
            taskId: currentTaskId || null,
          }
          : prev
      );

      if (onShowNotification) {
        onShowNotification({
          message: "Tarea actualizada correctamente",
          isError: false,
        });
      }

      handleCloseTaskModal();
    },
    [
      taskModalMessage,
      taskModalTask,
      resolveAssigneeObject,
      onShowNotification,
      handleCloseTaskModal,
    ]
  );

  // Determinar si mostrar el composer (debe estar antes de los returns)
  const composerClientId =
    resolvedSelectedClientId ||
    ((userRole === "agency_admin" || userRole === "consultant")
      ? selectedFilter
      : null);

  const shouldShowComposer =
    composerClientId &&
    ((userRole === "superadmin" && !!resolvedSelectedClientId) ||
      ["client", "consultant", "agency_admin"].includes(userRole)) &&
    generalThread &&
    !loadingThread;

  // Obtener cliente seleccionado para el composer
  const selectedClient = getClientById(composerClientId, clients);
  const isComposerReadOnly =
    userRole !== "client" && isClientReadOnly(currentUser, composerClientId);
  const shouldShowClientMeta =
    forceShowClientMeta ||
    (userRole || currentUser?.role || "").toLowerCase() !== "client";

  if (!canFetch) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 0",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error && !initialLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          alignItems: "center",
          justifyContent: "center",
          padding: "64px 24px",
        }}
      >
        <EmptyStateMessage
          icon={MessageSquare}
          message="No se pudieron cargar los mensajes recientes"
        />
        <Button
          variant="default"
          size="md"
          startIcon={<RefreshCw size={16} />}
          onClick={handleRetry}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  const headerSection = (
    <Box
      sx={{
        padding: "20px 20px 14px 20px",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {showSuperadminSelector ? "Mensajes recientes:" : "Mensajes recientes"}
        </Typography>
        {showSuperadminSelector && onSuperadminClientSelect && (
          <Box sx={{ minWidth: { xs: "100%", sm: 320 } }}>
            <CustomSelector
              value={superadminSelectorValue}
              onChange={onSuperadminClientSelect}
              options={superadminClientOptions}
              placeholder="Selecciona un cliente"
              searchable
            />
          </Box>
        )}
      </Stack>
    </Box>
  );

  const composerSection = shouldShowComposer ? (
    <Box
      sx={{
        padding: "16px 20px 0 20px",
      }}
    >
      <ThreadComposer
        onPost={handlePostToGeneralThread}
        placeholder={`Escribe un mensaje...`}
        clientData={{
          ...generalThread,
          name: selectedClient?.name,
        }}
        currentUser={currentUser}
        disabled={isComposerReadOnly}
        disabledPlaceholder="Cliente en solo lectura"
        consultants={generalThread?.participants || []}
        allUsers={generalThread?.participants || []}
        conversationFontSize={conversationFontSize}
      />
    </Box>
  ) : null;

  const messagesContent = isInitialLoading ? (
    <Box sx={{ padding: "12px 0" }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box
          key={`message-skeleton-${index}`}
          sx={{
            paddingTop: "16px",
            paddingBottom: "2px",
            borderBottom:
              index === 3 ? "none" : `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ padding: "0 20px 2px 20px" }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              sx={{ gap: "8px" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: "8px", flex: 1 }}
              >
                {shouldShowClientMeta && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="rounded" width={120} height={24} />
                  </Stack>
                )}
                <Skeleton variant="rounded" width={140} height={24} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="text" width={90} height={16} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={90} height={18} />
            </Stack>
          </Box>
          <Box sx={{ padding: "8px 20px 12px 20px" }}>
            <Skeleton variant="text" width="85%" height={20} />
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="rounded" width="100%" height={60} />
          </Box>
        </Box>
      ))}
    </Box>
  ) : messages.length === 0 && initialLoaded && !loadingPage ? (
    <Box sx={{ padding: "64px 32px" }}>
      <EmptyStateMessage icon={MessageSquare} message="Sin actividad reciente" />
    </Box>
  ) : (
    messages.map((message, index) => {
      const clientLabel = shouldShowClientMeta ? getClientLabel(message) : "";
      const threadLabel = getThreadLabel(message);
      const timestampLabel = formatDateTime(message.created_at);
      const totalReplies = Array.isArray(message.replies)
        ? message.replies.length
        : 0;
      const isLastMessage = index === messages.length - 1;
      const client = shouldShowClientMeta
        ? getClientById(message.client_id, clients)
        : null;
      const isMessageReadOnly =
        userRole !== "client" && isClientReadOnly(currentUser, message.client_id);

      return (
        <Box
          key={message.id}
          onClick={() => handleOpenMessage(message)}
          sx={{
            paddingTop: "16px",
            paddingBottom: "2px",
            borderBottom: isLastMessage
              ? "none"
              : `1px solid ${theme.palette.divider}`,
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: theme.palette.action.hover + "60",
            },
            "&:hover .hover-button": {
              opacity: 1,
              visibility: "visible",
            },
          }}
        >
          <Box
            sx={{
              padding: "0 20px 2px 20px",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              sx={{ gap: "8px" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: "8px", flex: 1 }}
              >
                {shouldShowClientMeta && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      src={getAvatarImageUrl(client?.logo) || undefined}
                      sx={{ width: 24, height: 24 }}
                    >
                      <AvatarFallback
                        sx={{
                          backgroundColor: brand.avatar,
                          color: "#fff",
                          fontSize: "0.625rem",
                          fontWeight: 600,
                        }}
                      >
                        {client?.name
                          ? client.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)
                          : clientLabel.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Chip
                      size="small"
                      label={clientLabel}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        height: "24px",
                        borderRadius: "16px",
                        backgroundColor: theme.palette.action.hover,
                      }}
                    />
                  </Stack>
                )}
                {isMessageReadOnly && <ReadOnlyBadge />}
                <Chip
                  size="small"
                  label={threadLabel}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    height: "24px",
                    borderRadius: "16px",
                    backgroundColor: theme.palette.action.hover,
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <Typography variant="caption">{timestampLabel}</Typography>
                  {totalReplies > 0 && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Circle size={6} fill={theme.palette.text.disabled} />
                      <Typography variant="caption">
                        {totalReplies}{" "}
                        {totalReplies === 1 ? "respuesta" : "respuestas"}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>

              <Box
                className="hover-button"
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenMessage(message);
                }}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  cursor: "pointer",
                  opacity: 0,
                  visibility: "hidden",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  color: theme.palette.text.primary,
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                  "&:active": {
                    color: theme.palette.primary.main,
                    animation: "pulseClick 0.4s ease",
                  },
                  "@keyframes pulseClick": {
                    "0%": {
                      color: theme.palette.text.primary,
                    },
                    "50%": {
                      color: theme.palette.primary.main,
                      transform: "scale(1.05)",
                    },
                    "100%": {
                      color: theme.palette.primary.main,
                      transform: "scale(1)",
                    },
                  },
                }}
              >
                Abrir hilo
                <ArrowRight size={14} />
              </Box>
            </Stack>
          </Box>

          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ cursor: "default" }}
          >
            <ThreadPost
              post={mapMessageToPost(message)}
              currentUser={currentUser}
              consultants={getMessageConsultants(message)}
              onLike={handleLikePost}
              onComment={handleCommentPost}
              onUpdateMessageType={handleUpdateMessageType}
              onDeleteAttachment={handleDeleteAttachment}
              onEditMessage={handleEditMessage}
              onDeleteMessage={handleDeleteMessage}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onShowNotification={onShowNotification}
              onAssignTask={handleOpenTaskModal}
              onArchiveEvent={handleOpenArchiveEventModal}
              allowAssignTask={shouldShowComposer}
              setMessageRef={setMessageRef}
              conversationFontSize={conversationFontSize}
              readOnly={isMessageReadOnly}
            />
          </Box>
        </Box>
      );
    })
  );

  return (
    <>
      {headerSection}

      {/* Contenedor scrollable de mensajes */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "0",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: theme.palette.text.secondary,
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.divider} transparent`,
        }}
      >
        {composerSection}
        {messagesContent}

        {error && initialLoaded && (
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.error.light}55`,
              backgroundColor: `${theme.palette.error.light}11`,
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.palette.error.dark, fontSize: "0.85rem" }}
            >
              {error}
            </Typography>
            <Button
              variant="outline"
              size="sm"
              startIcon={<RefreshCw size={14} />}
              onClick={handleRetry}
            >
              Reintentar
            </Button>
          </Box>
        )}

        {isLoadingMore && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "16px",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoadingMore && hasNextPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 3,
              mb: 2,
              px: "18px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingPage}
              startIcon={<ChevronDown size={16} />}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                px: 3,
                py: 1,
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
                "&:disabled": {
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.disabled,
                },
              }}
            >
              Página siguiente
            </Button>
          </Box>
        )}
      </Box>

      {/* Modal para asignar tarea - IDÉNTICO a ThreadInterface */}
      <EditTaskModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        task={
          taskModalTask || {
            id: null,
            title: "",
            description: "",
            assigned_to: "",
            deadline: null,
            documentation_urls: [],
          }
        }
        consultants={
          taskModalConsultants.length > 0
            ? taskModalConsultants
            : generalThread?.participants || []
        }
        hideTaskInfo
        onSubmitOverride={handleConversationTaskSubmit}
        onErrorPopup={
          onShowNotification
            ? (message) => onShowNotification({ message, isError: true })
            : undefined
        }
      />

      <ArchiveEventMessagesModal
        open={archiveEventModalOpen}
        onClose={() => {
          if (isArchivingEventMessages) return;
          closeArchiveEventModal();
        }}
        clientName={
          archiveEventTargetMessage?.client_name ||
          getClientById(archiveEventTargetMessage?.client_id, clients)?.name ||
          ""
        }
        targetMessage={archiveEventTargetMessage}
        recommendations={archiveEventRecommendations}
        selectedIds={selectedArchiveEventIds}
        onToggleSelection={handleToggleArchiveEventSelection}
        error=""
        onConfirm={handleConfirmArchiveEventMessages}
        isSubmitting={isArchivingEventMessages}
      />
    </>
  );
}
